import React, { createContext, useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import { RoleEnum, normalizeRole } from "./constants/roles";
import apiClient from "./api/apiClient";
import { getCurrentUserAccess } from "./api/auth.api";
import { MODULE_ACCESS_LEVEL_ORDER } from "./accessControl";

export const AuthContext = createContext();

const TOKEN_STORAGE_KEY = "access_token";
const LEGACY_BEARER_AUTH = String(process.env.REACT_APP_LEGACY_BEARER_AUTH || "false").toLowerCase() === "true";

// Match CurrentUserAccessResponse. Never turn a malformed 200 into trusted access.
const accessResponseSchema = z.object({
  id: z.number().int().positive(),
  username: z.string().min(1),
  legacy_role: z.nativeEnum(RoleEnum),
  access_profile: z.enum(["ADMIN", "MANAGER", "CONTRIBUTOR", "VIEWER", "AUDITOR", "CUSTOM"]),
  effective_access: z.record(z.enum(["NONE", "VIEW", "EDIT", "MANAGE"])),
  is_admin_equivalent: z.boolean(),
});

const decodeClaims = (token) => {
  if (!token || token.split(".").length !== 3) return null;
  try {
    const payload = token.split(".")[1];
    const json = atob(payload);
    return JSON.parse(json);
  } catch (_err) {
    return null;
  }
};

const roleDefaults = {
  [RoleEnum.ADMIN]: ["incident.view", "incident.create", "incident.edit", "incident.close", "incident.admin"],
  [RoleEnum.ANALYST]: ["incident.view", "incident.create", "incident.edit", "incident.close"],
  [RoleEnum.VIEWER]: ["incident.view"],
};

const loadToken = () => {
  if (LEGACY_BEARER_AUTH) {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (stored) return stored;
    const legacy = localStorage.getItem("token");
    if (legacy) {
      localStorage.setItem(TOKEN_STORAGE_KEY, legacy);
    }
    return legacy;
  }
  return localStorage.getItem("user") ? "cookie_session" : null;
};

const loadUser = () => {
  const saved = localStorage.getItem("user");
  const storedUser = saved ? JSON.parse(saved) : null;
  if (!LEGACY_BEARER_AUTH) {
    return storedUser;
  }
  const token = loadToken();
  const claims = decodeClaims(token) || {};
  if (!token || Object.keys(claims).length === 0) {
    return storedUser;
  }
  const role = normalizeRole(claims.role || storedUser?.role || RoleEnum.VIEWER, RoleEnum.VIEWER);
  return {
    username: claims.sub || storedUser?.username,
    role,
    permissions: claims.permissions || storedUser?.permissions || roleDefaults[role] || [],
    org_id: claims.org_id || storedUser?.org_id || null, // not a tenant boundary
    tenant_id: claims.tenant_id || storedUser?.tenant_id || null,
    access_profile: storedUser?.access_profile || null,
    effective_access: storedUser?.effective_access || null,
    is_admin_equivalent: Boolean(storedUser?.is_admin_equivalent),
  };
};

const persistUser = (nextUser) => {
  if (nextUser) {
    localStorage.setItem("user", JSON.stringify(nextUser));
  } else {
    localStorage.removeItem("user");
  }
};

const mergeAccessIntoUser = (baseUser, accessPayload) => {
  const resolvedRole = normalizeRole(
    accessPayload?.legacy_role || baseUser?.role || RoleEnum.VIEWER,
    RoleEnum.VIEWER
  );
  return {
    ...baseUser,
    username: accessPayload?.username || baseUser?.username,
    role: resolvedRole,
    permissions: baseUser?.permissions || roleDefaults[resolvedRole] || [],
    access_profile: accessPayload?.access_profile || baseUser?.access_profile || null,
    effective_access:
      accessPayload?.effective_access && typeof accessPayload.effective_access === "object"
        ? accessPayload.effective_access
        : null,
    is_admin_equivalent:
      accessPayload?.is_admin_equivalent !== undefined
        ? Boolean(accessPayload.is_admin_equivalent)
        : false,
  };
};

const accessWasReduced = (previous, next) =>
  previous?.username !== next.username ||
  previous?.role !== next.role ||
  (previous?.is_admin_equivalent && !next.is_admin_equivalent) ||
  Object.entries(previous?.effective_access || {}).some(
    ([module, level]) =>
      (MODULE_ACCESS_LEVEL_ORDER[next.effective_access?.[module]] || 0) <
      (MODULE_ACCESS_LEVEL_ORDER[level] || 0)
  );

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(loadToken);
  const [user, setUser] = useState(loadUser);
  const userRef = useRef(user);
  // Keep request snapshots current without re-fetching after access hydration.
  useEffect(() => { userRef.current = user; }, [user]);
  const [accessReady, setAccessReady] = useState(() => !(loadToken() && loadUser()));
  const [accessRefreshing, setAccessRefreshing] = useState(false);
  const [accessEpoch, setAccessEpoch] = useState(0);
  const [accessRefreshNonce, setAccessRefreshNonce] = useState(0);
  const accessRequest = useRef({ id: 0, pending: false });

  const clearAuthState = useCallback(() => {
    // Invalidate pending responses synchronously, including during logout.
    accessRequest.current.id += 1;
    accessRequest.current.pending = false;
    setAccessRefreshing(false);
    setToken(null);
    setUser(null);
    setAccessReady(true);
    if (LEGACY_BEARER_AUTH) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  }, []);

  const refreshAccess = useCallback(() => {
    setAccessRefreshNonce((current) => current + 1);
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    const onFocus = () => {
      // A focus burst must not repeatedly cancel/restart the same access check.
      if (!accessRequest.current.pending) refreshAccess();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [token, refreshAccess]);

  const login = (accessToken, username, role) => {
    accessRequest.current.id += 1;
    accessRequest.current.pending = false;
    setAccessRefreshing(false);
    const claims = decodeClaims(accessToken || "") || {};
    const resolvedRole = normalizeRole(claims.role || role || RoleEnum.VIEWER, RoleEnum.VIEWER);
    const resolvedUser = {
      username: claims.sub || username,
      role: resolvedRole,
      permissions: claims.permissions || roleDefaults[resolvedRole] || [],
      org_id: claims.org_id || null, // not a tenant boundary
      tenant_id: claims.tenant_id || null,
      access_profile: null,
      effective_access: null,
      is_admin_equivalent: false,
    };
    setToken(LEGACY_BEARER_AUTH ? accessToken || "cookie_session" : "cookie_session");
    setUser(resolvedUser);
    setAccessReady(false);
    if (LEGACY_BEARER_AUTH && accessToken) {
      localStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
      localStorage.removeItem("token");
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem("token");
    }
    persistUser(resolvedUser);
    refreshAccess();
  };

  const logout = async () => {
    // Capture legacy authorization before removing client credentials.
    const bearerToken = LEGACY_BEARER_AUTH ? localStorage.getItem(TOKEN_STORAGE_KEY) : null;
    const logoutConfig = { skipAuthRedirect: true };
    if (bearerToken) logoutConfig.headers = { Authorization: `Bearer ${bearerToken}` };
    clearAuthState();
    try {
      await apiClient.post("/auth/logout", {}, logoutConfig);
    } catch (_err) {
      // Best effort: local client state is always cleared.
    }
    window.location.href = "/login";
  };

  useEffect(() => {
    const requestUser = userRef.current;
    if (!token || !requestUser?.username) {
      setAccessReady(true);
      return undefined;
    }

    let cancelled = false;
    const requestState = accessRequest.current;
    const requestId = ++requestState.id;
    requestState.pending = true;
    // accessReady gates initial hydration/login only. Keep established routes and
    // drafts mounted while revalidating; a failed/denied result still revokes access.
    setAccessRefreshing(true);

    const hydrateAccess = async () => {
      try {
        const response = await getCurrentUserAccess();
        if (cancelled || requestId !== requestState.id) return;
        const accessPayload = accessResponseSchema.parse(response);
        const mergedUser = mergeAccessIntoUser(requestUser, accessPayload);
        // Confirmed reductions invalidate already-open privileged editors, even
        // where a module still has VIEW access. Ordinary refreshes keep this key.
        if (accessWasReduced(requestUser, mergedUser)) setAccessEpoch((current) => current + 1);
        setUser(mergedUser);
        persistUser(mergedUser);
        setAccessReady(true);
      } catch (err) {
        if (cancelled || requestId !== requestState.id) return;
        if ([401, 403].includes(Number(err?.status ?? err?.response?.status))) {
          clearAuthState();
          return;
        }

        // Preserve the deployed fail-closed policy for network/server/schema
        // failures. Cached privileges are not an offline authorization fallback.
        const fallbackUser = {
          ...requestUser,
          permissions: [],
          effective_access: null,
          is_admin_equivalent: false,
        };
        setAccessEpoch((current) => current + 1);
        setUser(fallbackUser);
        persistUser(fallbackUser);
        setAccessReady(true);
      } finally {
        if (!cancelled && requestId === requestState.id) {
          requestState.pending = false;
          setAccessRefreshing(false);
        }
      }
    };

    hydrateAccess();

    return () => {
      cancelled = true;
      if (requestId === requestState.id) requestState.pending = false;
    };
  }, [token, user?.username, user?.tenant_id, user?.role, accessRefreshNonce, clearAuthState]);

  return (
    <AuthContext.Provider value={{ token, user, login, logout, accessReady, accessRefreshing, refreshAccess }}>
      <React.Fragment key={accessEpoch}>{children}</React.Fragment>
    </AuthContext.Provider>
  );
};
